/**
 * VoyagePlanPickerSheet.js — Sub-ticket B
 *
 * Bottom-sheet modal for picking a Voyage Plan from the vessel's library
 * during Start Voyage. Mobile-first; falls back to a centered card on
 * desktop, in the same style as OverrideReasonModal.
 *
 * Surface (per CODING-BRIEF-B.md §5.2):
 *   - Header: "Pick a Voyage Plan" + close X.
 *   - "Clear plan" row (only when selectedPlanId != null).
 *   - Empty state when plans.length === 0 — copy directs user to Settings →
 *     Voyage Plans (no action button: Master may not be admin).
 *   - Search input ONLY when plans.length > 5 (small libraries don't need it).
 *   - Plan rows: name + item count + "Edited X ago". 56px min-height (B2 touch).
 *   - Tap row → onPick(planId) + onClose.
 *
 * Render-zero discipline (CORRECTION 2 / POB hotfix lesson):
 *   - All numeric counts use explicit ternaries / template strings, NEVER
 *     `{count && jsx}`. `{!!plans.length && jsx}` is permitted because the
 *     short-circuit returns `false` (boolean) before reaching React.
 *
 * Backward compat: this component reads from a `plans` array passed in by the
 * caller (already filtered to activePlans via useVoyagePlans). It does not
 * read voyage records, so the pre-B undefined-field concern does not apply.
 *
 * WebKit / iOS: outer container uses fixed positioning with internal scroll;
 * tested against Safari engine on staging.
 *
 * FleetSkipper - Built for Mariners.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { X, ClipboardList, Search, Check } from 'lucide-react';
import formatEditedAgo from '../../../../../utils/formatEditedAgo';

const SEARCH_THRESHOLD = 5;

const VoyagePlanPickerSheet = ({
  isOpen,
  onClose,
  plans = [],
  selectedPlanId = null,
  onPick,
  onEditPlan = null // optional — opens admin Voyage Plans editor (unused in v1, kept for forward extension)
}) => {
  const [query, setQuery] = useState('');

  const safePlans = Array.isArray(plans) ? plans : [];

  // Show search ONLY when the library is larger than the threshold (B2).
  const showSearch = safePlans.length > SEARCH_THRESHOLD;

  const filteredPlans = useMemo(() => {
    if (!showSearch || !query.trim()) return safePlans;
    const q = query.trim().toLowerCase();
    return safePlans.filter((p) =>
      typeof p?.name === 'string' && p.name.toLowerCase().includes(q)
    );
  }, [safePlans, query, showSearch]);

  const handleRowClick = useCallback(
    (planId) => {
      if (typeof onPick === 'function') onPick(planId);
      if (typeof onClose === 'function') onClose();
    },
    [onPick, onClose]
  );

  const handleClear = useCallback(() => {
    if (typeof onPick === 'function') onPick(null);
    if (typeof onClose === 'function') onClose();
  }, [onPick, onClose]);

  const handleClose = useCallback(() => {
    setQuery('');
    if (typeof onClose === 'function') onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center"
      data-testid="voyage-plan-picker-sheet"
    >
      <div
        className="bg-white w-full sm:max-w-lg sm:rounded-2xl max-h-[80vh] sm:max-h-[80vh] overflow-hidden flex flex-col"
        // Stop overlay clicks from propagating through the sheet body
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-cyan-700 px-5 py-4 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <ClipboardList className="h-5 w-5 text-white" />
            <h2 className="text-base font-semibold text-white">
              Pick a Voyage Plan
            </h2>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="p-2 hover:bg-cyan-600 rounded-lg transition-colors"
            aria-label="Close plan picker"
          >
            <X className="h-5 w-5 text-white" />
          </button>
        </div>

        {/* Search — only when library exceeds threshold */}
        {showSearch ? (
          <div className="px-4 pt-4 shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search plans by name"
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 text-sm"
                data-testid="voyage-plan-picker-search"
              />
            </div>
          </div>
        ) : null}

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {/* Empty state — explicit ternary, never short-circuit on length */}
          {safePlans.length === 0 ? (
            <div
              className="text-center py-8 px-4 text-sm text-gray-500"
              data-testid="voyage-plan-picker-empty"
            >
              No plans available — set up plans in{' '}
              <span className="font-medium text-gray-700">
                Settings → Voyage Plans
              </span>
              .
            </div>
          ) : (
            <>
              {/* Clear-plan row — only when something is selected. Ternary, never &&. */}
              {selectedPlanId ? (
                <button
                  type="button"
                  onClick={handleClear}
                  className="w-full min-h-[56px] flex items-center justify-between px-4 py-3 border border-gray-200 rounded-lg hover:bg-gray-50 text-left"
                  data-testid="voyage-plan-picker-clear"
                >
                  <span className="text-sm font-medium text-gray-700">
                    Clear plan
                  </span>
                  <X className="h-4 w-4 text-gray-400" />
                </button>
              ) : null}

              {/* No results after search filter */}
              {filteredPlans.length === 0 ? (
                <div className="text-center py-6 text-sm text-gray-400">
                  No plans match your search.
                </div>
              ) : (
                filteredPlans.map((plan) => {
                  const isSelected = plan.id === selectedPlanId;
                  const itemCount = Array.isArray(plan.items)
                    ? plan.items.length
                    : 0;
                  return (
                    <button
                      key={plan.id}
                      type="button"
                      onClick={() => handleRowClick(plan.id)}
                      className={
                        'w-full min-h-[56px] flex items-center justify-between px-4 py-3 border rounded-lg text-left transition-colors ' +
                        (isSelected
                          ? 'bg-cyan-50 border-cyan-300'
                          : 'border-gray-200 hover:bg-gray-50')
                      }
                      data-testid={`voyage-plan-picker-row-${plan.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {plan.name || 'Untitled plan'}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {itemCount === 1
                            ? '1 item'
                            : `${itemCount} items`}{' '}
                          · {formatEditedAgo(plan.updatedAt)}
                        </div>
                      </div>
                      {isSelected ? (
                        <Check className="h-5 w-5 text-cyan-600 shrink-0 ml-2" />
                      ) : null}
                    </button>
                  );
                })
              )}
            </>
          )}
        </div>

        {/* Optional footer slot for "Edit plan" forward-extension — unused in v1.
            Kept silent to avoid render-zero footguns. */}
        {typeof onEditPlan === 'function' && selectedPlanId ? (
          <div className="border-t border-gray-200 px-4 py-3 shrink-0">
            <button
              type="button"
              onClick={() => {
                onEditPlan(selectedPlanId);
                handleClose();
              }}
              className="text-sm text-cyan-700 hover:text-cyan-800 font-medium"
            >
              Edit this plan
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default VoyagePlanPickerSheet;
